import { injectable, inject } from 'inversify';
import { CredentialServiceImpl } from '../services/credential-service';
import { LocalStorageUtility } from '../utilities/localStorage.utility';
import { TYPES } from '../di/types';
import { AnalyticsPageviewPayload, GoogleAnalyticsPayload } from './types';

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class TealiumAnalyticsService {
  private static DOUBLE_QUOTES_REGEX = /"/g;
  private static readonly SCREEN_WIDTHS: { [key: string]: number } = {
    phone: 480,
    tablet: 768,
    desktop: 992,
  };

  private storageUtility = new LocalStorageUtility({
    storagePrefix: 'analytics',
  });
  private credentialService = CredentialServiceImpl.getInstance();

  constructor(
    @inject(TYPES.Window) private readonly windowInstance: Window,
    @inject(TYPES.AppConfig) private readonly appConfig: AppConfig,
  ) {}

  public logEvent(payload: GoogleAnalyticsPayload): void {
    const offeredSubscriptionId = payload.dataLayer?.offeredSubscriptionId;
    const activeSubscriptionId = payload.dataLayer?.activeSubscriptionId;

    const tealiumPayload: TealiumLinkPayload = {
      eventcategory: payload.category,
      eventaction: payload.action,
      eventlabel: payload.label,
      eventnon_interaction: payload.nonInteraction ? 'true' : '',
      interactiontype: 'view',
      ...this.getPageVersionFromPayload(payload),
      ...(this.getAccountInfo() as TealiumViewPayload),
      ...(offeredSubscriptionId && { offeredSubscriptionId }),
      ...(activeSubscriptionId && { activeSubscriptionId }),
    };

    if (!payload.forOnly || this.getPlatformType() === payload.forOnly) {
      this.windowInstance._utq.push(tealiumPayload);
    }
  }

  public logPageViewEvent(payload: AnalyticsPageviewPayload): void {
    const offeredSubscriptionId = payload.dataLayer?.offeredSubscriptionId;
    const activeSubscriptionId = payload.dataLayer?.activeSubscriptionId;

    const tealiumPayload: TealiumViewPayload = {
      pageGroup: payload.page_group,
      subGroup: payload.sub_group,
      pageName: payload.screen_name,
      eventcategory: '',
      eventaction: '',
      eventlabel: '',
      eventnon_interaction: '',
      interactiontype: 'view',
      ...this.getPageVersionFromPayload(payload),
      ...(this.getAccountInfo() as TealiumViewPayload),
      ...(offeredSubscriptionId && { offeredSubscriptionId }),
      ...(activeSubscriptionId && { activeSubscriptionId }),
    };

    if (!payload.forOnly || this.getPlatformType() === payload.forOnly) {
      this.windowInstance._utq.push(tealiumPayload);
    }
  }

  private getAccountInfo(): Partial<TealiumViewPayload> {
    return {
      dinertype: this.getAnalyticsValue('diner-type', 'diner_unknown'),
      userid: this.getAnalyticsValue('user-id', 'diner_unknown'),
      brand: this.appConfig.brand,
      loggedinstatus: this.credentialService.isLoggedIn()
        ? 'logged in'
        : 'not logged in',
      businesstype: 'consumer',
      platformtype: this.getPlatformType(),
      emailaddress: this.credentialService.getCredentialEmail(),
    };
  }

  private getAnalyticsValue(key: string, defaultValue?: string): string {
    const value = JSON.stringify(this.storageUtility.get(key)) || '';

    return (
      value.replace(TealiumAnalyticsService.DOUBLE_QUOTES_REGEX, '') ||
      defaultValue
    );
  }

  private getPlatformType(): TealiumPlatformType {
    const { phone, tablet } = TealiumAnalyticsService.SCREEN_WIDTHS;
    const width: number = this.getWindowWidth();
    let rv: TealiumPlatformType;

    if (width <= phone) {
      rv = 'browser_mobile';
    } else if (width <= tablet && width > phone) {
      rv = 'browser_tablet';
    } else {
      rv = 'browser_desktop';
    }

    return rv;
  }

  private getWindowWidth(): number {
    const { document } = this.windowInstance;
    return (
      this.windowInstance.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth
    );
  }

  private getPageVersionFromPayload(
    payload: AnalyticsPageviewPayload | GoogleAnalyticsPayload,
  ) {
    return payload.dataLayer?.pageVersion
      ? {
          pageVersion: payload.dataLayer.pageVersion,
        }
      : {};
  }
}
